
import { defineComponent } from 'vue';
import FormTextBox from './FormTextBox.vue';
import FormDropdown from './FormDropdown.vue';
import FormBoolean from './FormBoolean.vue';
import { Form } from 'vee-validate';
import * as yup from 'yup';
import debounce from '@/utils/debounce';
import ValidationErrors from '../components/ValidationErrors.vue';
import { generateValidationSummary, scrollTop, ValidationError } from '@/utils/api';

export default defineComponent({
    name: 'PreScreenForm',
    components: { FormTextBox, FormBoolean, FormDropdown, Form, ValidationErrors },
    emits: ['submit', 'change', 'ineligible'],
    props: {
        isAdmin: Boolean,
        initialData: Object as any,
        disabled: Boolean,
        isSubmitting: Boolean,
    },
    setup() {
        const validationHousehold = {
            isUnion: yup.boolean().required(),
            unionName: yup.string().when('isUnion', {
                is: true,
                then: () => yup.string().required(),
                otherwise: () => yup.string().notRequired()
            }),
            unionOther: yup.string().when('unionName', {
                is: 'Other',
                then: () => yup.string().required(),
                otherwise: () => yup.string().notRequired()
            }),
            unionLocal: yup.string().when('isUnion', {
                is: true,
                then: () => yup.string().required(),
                otherwise: () => yup.string().notRequired()
            }),
            source: yup.string().required(),
            sourceOther: yup.string().when('source', {
                is: 'Other',
                then: () => yup.string().required(),
                otherwise: () => yup.string().notRequired()
            }),
            hasChildcare: yup.boolean().required(),
            isLicensedProvider: yup.boolean().when('hasChildcare', {
                is: true,
                then: () => yup.boolean().required(),
                otherwise: () => yup.boolean().notRequired()
            }),
            hasOtherAdults: yup.boolean().required(),
            hasRelationship: yup.boolean().when('hasOtherAdults', {
                is: true,
                then: () => yup.boolean().required(),
                otherwise: () => yup.boolean().notRequired()
            }),
            numChildren: yup.number().required().integer('validation.number').min(0).typeError('validation.number').max(10),
            children: yup.array().of(
                yup.object().shape({
                    age: yup.string().required()
                })
            ).when('numChildren', {
                is: (val: number) => val > 0,
                then: () => yup.array().of(
                    yup.object().shape({
                        age: yup.string().required()
                    })
                ).required(),
                otherwise: () => yup.array().of(
                    yup.object().shape({
                        age: yup.string().notRequired()
                    })
                ).notRequired()
            }),
            numChildcare: yup.number().required().integer('validation.number').min(0).typeError('validation.number')
                    .max(10).test(
                        'childcare-max',
                        'validation.numChildcareMax',
                        (value, testContext) => { return value <= parseFloat(testContext.parent.numChildren) }
                    ),
            hasOtherParents: yup.boolean().required(),
            numParentOutside: yup.number().typeError('validation.number').when('hasOtherParents', {
                is: true,
                then: () => yup.number().required().integer('validation.number').min(0).typeError('validation.number'),
                otherwise: () => yup.number().integer('validation.number').min(0).typeError('validation.number').notRequired()
            }),
            isWorking: yup.boolean().required(),
            hoursPerWeek: yup.boolean().when('isWorking', {
                is: true,
                then: () => yup.boolean().required(),
                otherwise: () => yup.boolean().notRequired()
            }),
            proofOfEmployment: yup.boolean().when('hoursPerWeek', {
                is: false,
                then: () => yup.boolean().required(),
                otherwise: () => yup.boolean().notRequired()
            }),
        };

        const jobsSchema =  yup.array().of(
            yup.object().shape({
                frequency: yup.string().required(),
                amount: yup.string().currency() 
        }));

        const validationIncome = {
            jobs: jobsSchema.when('proofOfEmployment || hoursPerWeek', {
                is: true,
                then: () => jobsSchema.required(),
                otherwise: () => jobsSchema.notRequired()
            }),
            otherAdultJobs: jobsSchema.when('proofOfEmployment || hoursPerWeek', {
                is: true,
                then: () => jobsSchema.required(),
                otherwise: () => jobsSchema.notRequired()
            }),
            hasChildSupport: yup.boolean().required(),
            childSupportFrequency: yup.string().when('hasChildSupport', {
                is: true,
                then: () => yup.string().required(),
                otherwise: () => yup.string().notRequired()
            }),
            childSupportAmount: yup.string().when('hasChildSupport', {
                is: true,
                then: () => yup.string().currency()
            }),
            hasOtherIncome: yup.boolean().required(),
            otherIncome: yup.array().of(
                yup.object().shape({
                    type: yup.string().required(),
                    frequency: yup.string().required(),
                    amount: yup.string().currency()
                })
            ).when('hasOtherIncome', {
                is: true,
                then: () => yup.array().of(
                    yup.object().shape({
                        type: yup.string().label('otherIncomeType').required(),
                        frequency: yup.string().label('frequency').required(),
                        amount: yup.string().label('otherIncomeAmount').currency()
                    })
                ).required(),
                otherwise: () => yup.array().of(
                    yup.object().shape({
                        type: yup.string().notRequired(),
                        frequency: yup.string().notRequired(),
                        amount: yup.string().currency()
                    })
                ).notRequired()
            })
        };
        const unionOptions=[    
            "ATU",
            "Bricklayers",
            "Carpenters",
            "CSEA",
            "CWA",
            "DC37",
            "Firefighters",
            "IATSE",
            "IBEW",
            "Ironworkers",
            "LaborersLIUNA",
            "NY HTC",
            "NYSNA",
            "NYSUT",
            "OPEIU",
            "OperatingEngineers",
            "Painters",
            "PEF",
            "PlumbersUA",
            "RWDSU",
            "SAGAFTRA",
            "SEIU1199",
            "SEIU200United",
            "Sheetmetal Workers",
            "Steelworkers",
            "Teamsters",
            "TWU",
            "UAW",
            "UFCW",
            "WorkersUnited",
            "WritersGuild",
            "Other"
        ];
        const sourceOptions=[
            "advertisement",
            "childcareProvider",
            "ccrr",
            "dss",
            "prevRecipient",
            "union",
            "other"
        ];
        const ageOptions = [
            "newborn","1","2","3","4","5","6",
            "7","8","9","10","11","12","13","14","15","16",
            "17","18","19","19plus"
        ];
        const frequencies=[
            "weekly",
            "biWeekly",
            "semiMonthly",
            "monthly",
            "annually"
        ];
        const otherIncomeOptions=[
            "alimony",
            "unemployment",
            "ssi",
            "disability",
            "rental",
            "dividends",
            "pa",
            "safetyNet"
        ];
        return { validationHousehold, validationIncome, unionOptions, sourceOptions, ageOptions, frequencies, otherIncomeOptions }
    },
    data() {
        const children : {
            age: string
        }[] = []
        const jobs : {
            frequency: string,
            amount: number
        }[] = [{frequency:'', amount:0}]
        const otherAdultJobs : {
            frequency: string,
            amount: number
        }[] = [{frequency:'', amount:0}]
        const otherIncome : {
            type: string,
            frequency: string,
            amount: number
        }[] = [{type:'', frequency:'', amount:0}]
        
        return {
            preScreen: {
                dbVersionNumber: undefined,
                isUnion: undefined, // radio
                unionName: '', // ddl
                unionOther: '',
                unionLocal: '',
                source: '', // ddl
                sourceOther: '',
                hasChildcare: undefined, // radio
                isLicensedProvider: undefined, // radio
                hasOtherAdults: undefined, // radio
                hasRelationship: undefined, // radio
                numChildren: undefined,
                children,
                numChildcare: undefined,
                hasOtherParents: undefined, // radio
                numParentOutside: undefined,
                isWorking: undefined, // radio
                hoursPerWeek: undefined,
                proofOfEmployment: undefined, // radio
                jobs,
                otherAdultJobs,
                hasChildSupport: undefined, // radio
                childSupportFrequency: '', // ddl
                childSupportAmount: undefined,
                hasOtherIncome: undefined, // radio
                otherIncome,
                otherInformation: '', // textarea
            },
            validationErrors: [] as ValidationError[]
        }
    },
    methods: {
        addChildren: debounce(function(this: any, newCount: number) {
            const existingCount = this.preScreen.children.length;
            if(isNaN(newCount)) {
                newCount = 0;
            }
            
            if (newCount > existingCount) {
                for (let i = 0; i < newCount - existingCount; i++) {
                    this.preScreen.children.push({age:""});
                }
            } else if (newCount < existingCount) {
                for (let i = 0; i < existingCount - newCount; i++) {
                    this.preScreen.children.pop();
                }
            }
        }, 500),
        addJob() {
            this.preScreen.jobs.push({frequency:'', amount:0});
        },
        removeJob() {
            this.preScreen.jobs.pop();
        },
        addOtherJob() {
            this.preScreen.otherAdultJobs.push({frequency:'', amount:0});
        },
        removeOtherJob() {
            this.preScreen.otherAdultJobs.pop();
        },
        addOtherIncome() {
            this.preScreen.otherIncome.push({type:'', frequency:'', amount:0});
        },
        removeOtherIncome() {
            this.preScreen.otherIncome.pop();
        },
        handleSubmit() {
            this.resetValidation();
            if(this.isEligibleWork) {
                this.$emit('submit', this.preScreen);
            } else {
                this.$emit('ineligible', 'errors.ineligibleWork', this.preScreen);
            }
        },
        handleInvalid(data: any) {
            const { errors } = data;
            this.resetValidation();
            console.log(errors);
            console.log(data);
            this.validationErrors = generateValidationSummary(errors);
            scrollTop();
        },
        resetValidation() {
            this.validationErrors = [];
        }   
    },
    watch: {
        'preScreen.numChildren': function(value) {
            this.addChildren(parseInt(value) > 10 ? 10 : parseInt(value));
        },
        preScreen: {
            handler: function() {
                this.$emit('change', this.preScreen);
            },
            deep: true
        }
    },
    computed: {
        isEligibleWork() {
            return this.preScreen.isWorking && (this.preScreen.hoursPerWeek || this.preScreen.proofOfEmployment);
        },
        validationSchema() {
            let schema = yup.object(this.validationHousehold);
            if(this.isEligibleWork) {
                schema = schema.shape(this.validationIncome)
            }
            return schema;
        },
        hasOtherAdultIncome() {
            if(this.preScreen){
                return this.preScreen.hasOtherAdults && this.preScreen.hasRelationship;
            }
            return false;
        }
    },
    created() {
        if(this.initialData) {
            console.log(this.initialData);
            Object.assign(this.preScreen, this.initialData);
        }
    },
    mounted() {
        const w: any = window;
        w.$('.tooltip').tooltipster({
            theme: 'tooltipster-WDI',
            delay: 100,
            animation: 'grow',
            trigger: 'click',
            maxWidth: '250'
        });
    },

});
